import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  ListItem,
  List,
  useMediaQuery,
  Container,
  Button,
  Link as Link1,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Link } from '../../assets/images/externalLink.svg';
import { visuallyHidden } from '@mui/utils';
import ListItemText from '@mui/material/ListItemText';
import arrowLeft from '../../assets/images/arrows/chevron-left.png';
import arrowRight from '../../assets/images/arrows/chevron-right.png';

// Replace old constants
const DESKTOP_CARD_WIDTH = 380;
const TABLET_CARD_WIDTH = 380;
const MOBILE_CARD_WIDTH = 272;

interface CardData {
  id: number;
  header: string;
  img: string;
  description: string;
  description2: string;
  listItems: Array<string>;
  listTitle: string;
  alt: string;
  cta: any;
  url: string;
}
interface CarouselData {
  header: string;
  body: string;
  cards: Array<CardData>;
}

interface FeatureCardProps extends CardData {
  cardWidth: number;
  isFirst?: boolean;
  headingId?: string;
  label?: string;
  totalCards?: number;
  tabIndex?: number;
  'aria-hidden'?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  position: any;
  total: number;
}

function renderWithExternalLinks(text: string) {
  return text.split(/(<a.*?<\/a>)/g).map((part, index) => {
    let url = part.match(/href='([^']+)'/)?.[1];
    let linkText = part.match(/<a[^>]*>(.*?)<\/a>/)?.[1];

    if (!linkText) return part;

    return (
      <Button
        key={`button-${index}`}
        component={Link1}
        target="_blank"
        rel="noopener noreferrer"
        variant="text"
        href={url}
        sx={{
          fontSize: '14px',
          verticalAlign: 'baseline !important',
          '&.Mui-focusVisible': {
            outline: '2px solid black',
            outlineOffset: '2px'
          },
          textAlign: 'left'
        }}
      >
        {linkText}
      </Button>
    );
  });
}

const FeatureCard = React.forwardRef<HTMLLIElement, FeatureCardProps>(
  (
    {
      id,
      header,
      label,
      totalCards,
      img,
      alt,
      description,
      description2,
      listTitle,
      listItems,
      cardWidth,
      cta,
      url,
      tabIndex,
      'aria-hidden': ariaHidden,
      onKeyDown,
    },
    ref
  ) => {
    //const parts = content.text.split(/(<a>.*?<\/a>)/g);
    const isMobile = useMediaQuery('(max-width: 700px)');

    const body = renderWithExternalLinks(description);

    return (
      <ListItem
        ref={ref}
        component="li"
        // tabIndex={tabIndex}
        aria-hidden={ariaHidden}
        onKeyDown={onKeyDown}
        aria-posinset={id + 1}
        aria-setsize={totalCards}
        {...(ariaHidden ? ({ inert: '' } as any) : {})}
        sx={{
          maxWidth: isMobile ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH,
          // maxHeight: '524px',
          flexShrink: 0,
          borderRadius: '16px',
          padding: 0,
          backgroundColor: '#fff',
          boxSizing: 'border-box',
          border: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          listStyle: 'none',
          boxShadow: '0 0 15px 0 rgba(0,0,0,0.15)',
          position: 'relative',
          '&:focus-visible': {
            outline: '3px solid #1460AA',
            outlineOffset: '4px'
          }
        }}
        disableGutters
      >
        {/* Features (not tabbable initially) */}
        <Box
          sx={{
            listStyle: 'none',
            height: '100%',
            margin: 0,
            paddingLeft: '0px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            //justifyContent: 'space-between',
            gap: '4px'
          }}
        >
          <Box
             component="span"
             sx={visuallyHidden}
           >{`${label} ${id + 1} of ${totalCards}`}</Box>
          {/* Header */}
          <img
            src={img}
            alt={alt}
            style={{
              width: '100%',
              display: 'block',
              height: isMobile ? '140px' : '190px',
              marginBottom: 16,
              objectFit: 'cover',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px'
            }}
          />
          <Box
            className="card-content"
            sx={{
              padding: '0px 24px',
              alignSelf: 'flex-start',
              display: 'flex',
              minHeight: '264px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '10px'
            }}
          >
            <Typography
              id={`card-heading-${id}`}
              variant="h3"
              sx={{
                // fontFamily: 'Open Sans',
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '24px'
              }}
            >
              {header}
              {/* <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {`${position} of ${total}`}
          </Box> */}
            </Typography>
            <Box
              //key={f.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: '10px',
                outline: 'none',
                position: 'relative',
                '&:focus-visible': {
                  outline: '2px solid #1460AA',
                  borderRadius: '4px'
                }
              }}
            >
              {description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#101828',
                    //  fontFamily: 'Open Sans',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}
                >
                  {body}
                </Typography>
              )}
              {description2 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#101828',
                    //  fontFamily: 'Open Sans',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}
                >
                  {description2}
                </Typography>
              )}
              {listTitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#101828',
                    //  fontFamily: 'Open Sans',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}
                >
                  {listTitle}
                </Typography>
              )}
              {listItems && (
                <List
                  component="ul"
                  role="list"
                  sx={{
                    pl: 1,
                    display: 'flex',
                    gap: '0px',
                    flexDirection: 'column',
                    '& .MuiListItem-root': { mb: '6px' }
                  }}
                  disablePadding
                >
                  {listItems.map((listitem, index) => {
                    return (
                        <ListItem
                          key={index}
                          component="li"
                          disableGutters
                          disablePadding
                        >
                          <ListItemText
                            sx={{ margin: 0 }}
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '16px',
                                  gap: '5px'
                                }}
                              >
                                <Box
                                  sx={{ fontSize: '15px', flexShrink: 0 }}
                                  component="span"
                                >
                                  •
                                </Box>
                                {listitem}
                                <Box
                                  component="span"
                                  sx={visuallyHidden}
                                >{`item ${index + 1} of ${listItems.length}`}</Box>
                              </Typography>
                            }
                          ></ListItemText>
                        </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'left' }}>
        {cta && (
          <Button
            id={`card-button-${id}`}
            component="a"
            href={cta.url}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(e) => e.stopPropagation()}
            variant="text"
            aria-labelledby={`card-button-${id} card-heading-${id}`}
            sx={{
              color: '#1460AA',
              padding: '24px',
              mt: 'auto',
              '&.Mui-focusVisible': {
                outline: '2px solid black',
                outlineOffset: '2px'
              }
            }}
            endIcon={<Link />}
          >
            {cta.label}
          </Button>
        )}
        </Box>
        {/* </Box> */}
      </ListItem>
    );
  }
);

FeatureCard.displayName = 'FeatureCard';

const SingleCarousel: React.FC<{ carouselData: CarouselData }> = ({
  carouselData
}) => {
  const { t } = useTranslation();
  const [startIndex, setStartIndex] = useState(0);
  // NEW drag states
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = `section-heading`;
  const regionId = `carousel-region`;

  const isMobile = useMediaQuery('(max-width: 700px)');
  const isTablet = useMediaQuery('(min-width:701px) and (max-width:1024px)');
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const [isDesktopScreen, setIsDesktopScreen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const totalCards = carouselData.cards.length;

  useEffect(() => {
    const checkZoom = () => {
      setWindowWidth(window.innerWidth);
      // outerWidth/innerWidth gives the effective zoom ratio in all major browsers.
      // Both values are in CSS pixels, so this works correctly on all devices
      // (standard 1x, HiDPI Surface, Retina MacBook) without a per-device baseline.
      // > 1.05 threshold: avoids false positives from scrollbar width (~15px)
      // while reliably catching 110% zoom (ratio ≈ 1.1).
      // Works even when the page is loaded at a non-100% zoom level.
      const isZoomedIn = window.outerWidth / window.innerWidth > 1.05;
      setIsDesktopScreen(window.screen.width > 1024 && isZoomedIn);
    };

    // Chrome/Edge/Firefox fire 'resize' when browser zoom changes.
    window.addEventListener('resize', checkZoom);

    // Safari does not fire 'resize' on browser zoom. Use the MDN-recommended
    // matchMedia resolution-change pattern as a fallback.
    let mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const onDprChange = () => {
      checkZoom();
      // Re-subscribe with the new DPR value for the next change.
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mql.addEventListener('change', onDprChange, { once: true });
    };
    mql.addEventListener('change', onDprChange, { once: true });

    checkZoom(); // run immediately on mount

    return () => {
      window.removeEventListener('resize', checkZoom);
      mql.removeEventListener('change', onDprChange);
    };
  }, []);

  // When zoom state toggles (e.g. user zooms back out to 100%), reset startIndex to 0
  // so a stale offset doesn't leave the carousel permanently shifted.
  useEffect(() => {
    setStartIndex(0);
    setDragOffset(0);
  }, [isDesktopScreen]);

  const cardWidth = isTablet ? TABLET_CARD_WIDTH : isMobile ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH;
  // const isLargeTab =useMediaQuery('(min-width:901px) and (max-width:1024px)');
  const cardOffset = cardWidth + 32;
  const targetVisibleCards = isMobile ? 1.85 : isTablet ? 2.5 : 3;
  const desktopVisibleCards = Math.max(1, Math.floor((windowWidth + 32) / cardOffset));
  // isDesktopWidth: physical screen is a desktop regardless of zoom or innerWidth shrinkage.
  const isDesktopWidth = typeof window !== 'undefined' && window.screen.width > 1024;
  // supportContentContainer has max-width: 1200px.
  // When windowWidth < 1200, the container is constrained (narrower than its max),
  // meaning it's either a smaller desktop viewport or the user has zoomed in enough
  // to push it below 1200px. This is the reliable cross-device trigger for navigation.
  const containerNarrowerThan1200 = windowWidth < 1200;
  // Peek mode: activate when on desktop AND container is constrained OR already zoomed.
  // Forces effectiveVisibleCards to totalCards - 0.85 so maxShift > 0 and buttons scroll.
  const allDesktopCardsFit =
    (isDesktopScreen || (isDesktopWidth && containerNarrowerThan1200)) &&
    desktopVisibleCards >= totalCards;
  
  const effectiveVisibleCards = isMobile || isTablet
    ? targetVisibleCards
    : allDesktopCardsFit
      ? totalCards - 0.85
      : Math.min(3, desktopVisibleCards);
  // Transform carousel data to card data

  // Show nav buttons when:
  //  - zoomed above 100% on a desktop screen (isDesktopScreen), OR
  //  - desktop screen AND container is narrower than 1200px (isDesktopWidth && containerNarrowerThan1200).
  // Exclude likely mobile/phone devices (high DPR small screens).
  const isLikelyMobileDevice = typeof window !== 'undefined' && window.devicePixelRatio > 2.5 && window.screen.width <= 1024;
  const showNavButtons =
    (isDesktopScreen || (isDesktopWidth && containerNarrowerThan1200)) &&
    !isLikelyMobileDevice;

  const rawVisibleCardsForShift = Math.max(1, Math.ceil(effectiveVisibleCards));
  const visibleCardsForShift =
    showNavButtons && totalCards > 1
      ? Math.min(rawVisibleCardsForShift, totalCards - 1)
      : rawVisibleCardsForShift;
  const maxShift = Math.max(0, totalCards - visibleCardsForShift);

  // Boundary state — no wrap-around. Prev is disabled at the start, Next at the end.
  const isPrevDisabled = startIndex === 0;
  const isNextDisabled = startIndex >= maxShift;

  const focusWithoutScroll = (element: HTMLElement | null) => {
    if (!element) return;
    element.focus({ preventScroll: true });
  };

  // Handle keyboard navigation for cards
  const handleCardKeyDown = (event: React.KeyboardEvent, cardIndex: number) => {
    const visibleStartIndex = Math.floor(startIndex);
    const visibleEndIndex = Math.min(
      totalCards - 1,
      Math.ceil(startIndex + effectiveVisibleCards) - 1
    );
    const isFirstVisible = cardIndex === visibleStartIndex;
    const isLastVisible = cardIndex === visibleEndIndex;

    const cardElement = event.currentTarget as HTMLElement;
    const focusableElements = Array.from(
      cardElement.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('inert') && !el.getAttribute('aria-hidden'));

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = event.target as HTMLElement;
    const isOnFirstFocusable = !!firstFocusable && activeElement === firstFocusable;
    const isOnLastFocusable = !!lastFocusable && activeElement === lastFocusable;

    if (event.key === 'Tab') {
      if (!event.shiftKey && isLastVisible && isOnLastFocusable) {
        // Only redirect focus to Next button if it is enabled (not disabled)
        if (showNavButtons && !isNextDisabled) {
          event.preventDefault();
          focusWithoutScroll(nextButtonRef.current);
        }
      } else if (event.shiftKey && isFirstVisible && isOnFirstFocusable) {
        // Only redirect focus to Prev button if it is enabled (not disabled)
        if (showNavButtons && !isPrevDisabled) {
          event.preventDefault();
          focusWithoutScroll(prevButtonRef.current);
        }
      }
    }
  };

  const focusFirstInCard = (card: HTMLLIElement) => {
    const focusable = card.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusWithoutScroll(focusable ?? card);
  };

  const handleNext = () => {
    if (isNextDisabled) return;
    setDragOffset(0); // reset any drag remainder
    const newIndex = Math.min(startIndex + 1, maxShift);
    const firstVisible = newIndex + 1;
    const lastVisible = Math.min(newIndex + Math.ceil(effectiveVisibleCards), totalCards);
    flushSync(() => {
      setStartIndex(newIndex);
    });
    const targetCardIndex = Math.min(newIndex + 2, totalCards - 1);
    const targetCard = cardRefs.current[targetCardIndex];
    if (targetCard) {
      focusFirstInCard(targetCard);
    }
    setTimeout(() => {
      setAnnouncement(t('basepage.support.showingCards', { firstVisible, lastVisible, totalCards }) as string || '');
    }, 50);
  };

  const handlePrev = () => {
    if (isPrevDisabled) return;
    setDragOffset(0); // reset any drag remainder
    const newIndex = Math.max(startIndex - 1, 0);
    const firstVisible = newIndex + 1;
    const lastVisible = Math.min(newIndex + Math.ceil(effectiveVisibleCards), totalCards);
    flushSync(() => {
      setStartIndex(newIndex);
    });
    const targetCard = cardRefs.current[newIndex];
    if (targetCard) {
      focusFirstInCard(targetCard);
    }
    setTimeout(() => {
      setAnnouncement(t('basepage.support.showingCards', { firstVisible, lastVisible, totalCards }) as string || '');
    }, 50);
  };

  // Container width: in peek-mode use effectiveVisibleCards so the partial card is clipped;
  // otherwise use targetVisibleCards (the design default for that breakpoint).
  const viewportWidth = allDesktopCardsFit
    ? effectiveVisibleCards * cardWidth + 2 * 32
    : targetVisibleCards * cardWidth + 2 * 32;

  const canDrag = totalCards > effectiveVisibleCards;
  const dragThreshold = cardOffset * 0.15; // need to drag card

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canDrag) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging || dragStartX === null) return;
    const delta = e.clientX - dragStartX;

    // Clamp when at boundaries
    if (startIndex === 0 && delta > 0) {
      setDragOffset(delta * 0.35); // resistance
    } else if (startIndex === maxShift && delta < 0) {
      setDragOffset(delta * 0.35);
    } else {
      setDragOffset(delta);
    }
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!isDragging) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);

    if (dragOffset <= -dragThreshold) {
      handleNext();
    } else if (dragOffset >= dragThreshold) {
      handlePrev();
    } else {
      // Snap back
      setDragOffset(0);
    }
    setDragStartX(null);
  };

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ width: '100%', py: 2 }}
      ref={viewportRef}
    >
      <Box aria-live="polite" aria-atomic="true" 
      sx={visuallyHidden}
      >
        {announcement}
      </Box>
      {/* Heading aligned with container */}
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 4,
          gap: '16px'
        }}
        disableGutters
      >
        <Typography
          component="h2"
          id={headingId}
          variant="h3"
          sx={{
            // fontFamily: 'Open Sans',
            fontWeight: '700 !important',
            fontSize: '28px !important',
            lineHeight: '36px'
          }}
        >
          {carouselData.header}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '18px'
          }}
        >
          {carouselData.body}
        </Typography>
      </Container>
      {/* Carousel extends beyond container */}
      <Box sx={{ position: 'relative' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', maxWidth:`${viewportWidth}` }} disableGutters>
          <Box>
            <Box
              // component="ul"
              role="group"
              aria-label={t('basepage.support.cardListLabel', { count: carouselData.cards.length }) as string}
              className="card-grid"
              sx={{
                display: 'flex',
                transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
                transform: `translateX(calc(-${startIndex * cardOffset}px + ${dragOffset}px))`,
                width: 'fit-content',
                padding: 0,
                margin: 0,
                gap: '32px',
                listStyle: 'none',
                userSelect: 'none',
                touchAction: 'pan-y'
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {carouselData?.cards?.map((card, index) => {
                const isFullyVisible = index >= Math.floor(startIndex) && index < Math.ceil(startIndex + effectiveVisibleCards);
                return (
                  <FeatureCard
                    key={card.id}
                    {...card}
                    cardWidth={cardWidth}
                    label={carouselData['label']}
                    totalCards={carouselData.cards.length}
                    ref={(el) => {
                      cardRefs.current[index] = el;
                    }}
                    isFirst={index === 0}
                    headingId={headingId}
                    tabIndex={isFullyVisible ? 0 : -1}
                    aria-hidden={!isFullyVisible}
                    onKeyDown={(event) => handleCardKeyDown(event, index)}
                    position={`item ${index + 1}`}
                    total={totalCards}
                  />
                );
              })}
            </Box>
          </Box>
        </Container>
      </Box>
      
      {/* Navigation buttons - show when cards don't all fit */}
      {showNavButtons && (
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'end', mt: 4 }} disableGutters>
          <Box
            sx={{
              display: 'flex',
              gap: 0.2,
              backgroundColor: '#FFFFFF !important',
              borderRadius: '999px',
              boxShadow: '0px 0px 15px 0px #00000026'
            }}
          >
            <IconButton
              ref={prevButtonRef}
              disabled={isPrevDisabled}
              aria-label={t('basepage.support.prevLabel')}
              tabIndex={isPrevDisabled ? -1 : 0}
              aria-controls={regionId}
              // aria-disabled={isPrevDisabled}
              aria-hidden={isPrevDisabled}
              {...(isPrevDisabled ? ({ inert: '' } as any) : {})}
              onClick={handlePrev}
              sx={{
                backgroundColor: 'white',
                boxShadow: '0px 0px 15px 0px #00000026',
                opacity: isPrevDisabled ? 0.38 : 1,
                cursor: isPrevDisabled ? 'not-allowed' : 'pointer',
                '&:hover': { backgroundColor: 'white' },
                '&:focus-visible': {
                  outline: '2px solid black',
                  outlineOffset: '2px'
                },
                padding: { xs: 1, sm: 1 }
              }}
            >
              {!isPrevDisabled ? (
                <img src={arrowRight} alt="" style={{ rotate: '180deg' }} />
              ) : (
                <img src={arrowLeft} alt="" />
              )}
            </IconButton>
            <IconButton
              ref={nextButtonRef}
              disabled={isNextDisabled}
              aria-label={t('basepage.support.nextLabel')}
              aria-controls={regionId}
              // aria-disabled={isNextDisabled}
              aria-hidden={isNextDisabled}
              {...(isNextDisabled ? ({ inert: '' } as any) : {})}
              tabIndex={isNextDisabled ? -1 : 0}
              onClick={handleNext}
              sx={{
                backgroundColor: 'white',
                boxShadow: '0px 0px 15px 0px #00000026',
                opacity: isNextDisabled ? 0.38 : 1,
                cursor: isNextDisabled ? 'not-allowed' : 'pointer',
                '&:hover': { backgroundColor: 'white' },
                '&:focus-visible': {
                  outline: '2px solid black',
                  outlineOffset: '2px'
                },
                padding: { xs: 1, sm: 1 }
              }}
            >
              {!isNextDisabled ? (
                <img src={arrowRight} alt="" />
              ) : (
                <img src={arrowLeft} alt="" style={{ rotate: '180deg' }} />
              )}
            </IconButton>
          </Box>
        </Container>
      )}
    </Box>
  );
};

const SupportCards = () => {
  const { t } = useTranslation();
  const data: any = t('basepage.support.supportCarousel', {
    returnObjects: true
  });
  return (
    <Box sx={{ width: '100%' }}>
      <SingleCarousel carouselData={data} />
    </Box>
  );
};

export default SupportCards;